// Injectable random number generator. Systems take an Rng so tests can
// pass a seeded generator and get deterministic results.

export type Rng = () => number

/** Seeded PRNG (mulberry32). Returns floats in [0, 1). */
export function createRng(seed: number): Rng {
  let state = seed >>> 0
  return () => {
    state = (state + 0x6d2b79f5) >>> 0
    let t = state
    t = Math.imul(t ^ (t >>> 15), t | 1)
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

export const defaultRng: Rng = Math.random

/** Deterministic 32-bit hash of a string — used to seed per-entity RNGs
 *  (idempotent save migrations) and to assign stable avatar variants. */
export function hashString(value: string): number {
  let hash = 0
  for (let i = 0; i < value.length; i++) {
    hash = (hash * 31 + value.charCodeAt(i)) >>> 0
  }
  return hash
}

/** Pick an item given parallel weights (weights need not sum to 1). */
export function rollWeighted<T>(items: readonly T[], weights: readonly number[], rng: Rng = defaultRng): T {
  const total = weights.reduce((sum, w) => sum + w, 0)
  let roll = rng() * total
  for (let i = 0; i < items.length; i++) {
    roll -= weights[i]
    if (roll <= 0) return items[i]
  }
  return items[items.length - 1]
}

/** Random integer in [min, max] inclusive. */
export function rollInt(min: number, max: number, rng: Rng = defaultRng): number {
  return Math.floor(rng() * (max - min + 1)) + min
}
